import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import apiClient from '../services/api'

interface User {
  id: string
  email: string
  role: string
  permissions?: Record<string, boolean>
  is_superuser?: boolean
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  setUser: (user: User) => void
  hasPermission: (permission: string) => boolean
  hasAnyPermission: (permissions: string[]) => boolean
  hasRole: (role: string) => boolean
  canAccessConfiguration: () => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null as any,
      token: null as any,
      isAuthenticated: false,

      login: async (email: string, password: string) => {
        try {
          const response = await apiClient.post('/admin/auth/login', {
            email,
            password,
          })

          const { user, access_token } = response.data

          set({
            user: {
              id: user.id,
              email: user.email,
              role: user.role || 'user',
              permissions: user.permissions || {},
              is_superuser: user.is_superuser || false,
            },
            token: access_token,
            isAuthenticated: true,
          })

          // Set token in API client for subsequent requests
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`
        } catch (error: any) {
          // Improved error handling with specific messages
          if (error.response) {
            // Server responded with error
            const message = error.response.data?.detail || 'Login failed'
            throw new Error(message)
          } else if (error.request) {
            // Request made but no response
            throw new Error('Unable to connect to server. Please check your connection.')
          } else {
            // Something else happened
            throw new Error('An unexpected error occurred during login')
          }
        }
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        })

        // Remove token from API client
        delete apiClient.defaults.headers.common['Authorization']
      },

      setUser: (user: User) => {
        set({ user })
      },

      hasPermission: (permission: string) => {
        const { user } = get()
        if (!user) return false
        if (user.is_superuser) return true
        return user.permissions?.[permission] === true
      },

      hasAnyPermission: (permissions: string[]) => {
        const { user } = get()
        if (!user) return false
        if (user.is_superuser) return true
        return permissions.some(perm => user.permissions?.[perm] === true)
      },

      hasRole: (role: string) => {
        const { user } = get()
        if (!user) return false
        return user.role?.toLowerCase() === role.toLowerCase()
      },

      canAccessConfiguration: () => {
        const { user, hasRole, hasAnyPermission } = get()
        if (!user) return false
        if (user.is_superuser) return true

        // Check for the real IT Administrator role
        if (hasRole('IT Administrator')) {
          return true
        }

        // Check for the real underlying permissions
        return hasAnyPermission(['admin.system.config', 'integrations.read'])
      },
    }),
    {
      name: 'auth-storage',
      version: 1,
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
      migrate: (persistedState: any, version: number) => {
        // Handle migration from older versions if needed
        if (version === 0) {
          // Migration logic for version 0 to 1
          return persistedState;
        }
        return persistedState;
      },
    }
  )
)